import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as XLSX from 'xlsx'
import FiltersSection from '../components/FiltersSection'
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import { surface } from '../components/ui/surface'
import axiosInstance from '../utils/axiosInstance'

const getTableColumns = (t) => [
  { key: 'school', label: t('devices.object') },
  { key: 'timestamp', label: t('speedtestResults.date') },
  { key: 'download', label: t('devices.download') },
  { key: 'upload', label: t('devices.upload') },
  { key: 'loss', label: t('speedtestResults.packetLoss') },
];

const DATEPICKER_PORTAL_ID = 'speedtest-datepicker-portal';

const SpeedtestResults = ({ setIsAuthenticated, userRole, userEmail, onLogout }) => {
  const { t } = useTranslation();
  const [results, setResults] = useState([]);
  const tableColumns = getTableColumns(t);
  const [schoolList, setSchoolList] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [ipMap, setIpMap] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isSidebarVisible, setSidebarVisible] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return true;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const itemsPerPage = 20;

  const toggleSidebar = () => setSidebarVisible(!isSidebarVisible);

  const isFiltered = useMemo(() => Boolean(selectedSchool || startDate || endDate), [selectedSchool, startDate, endDate]);

  const fetchResults = async (filters = {}) => {
    try {
      const params = {
        ...filters,
        page: currentPage,
        limit: itemsPerPage,
      };
      const response = await axiosInstance.get('/metrics/speedtest-results', {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      setResults(response.data.data || []);
      // Используем total из пагинации, если он есть
      if (response.data.pagination?.total !== undefined) {
        setTotalResults(response.data.pagination.total);
      } else {
        // Fallback на длину массива, если пагинация не пришла
        setTotalResults(response.data.data?.length || 0);
      }
    } catch (error) {
    }
  };

  const fetchSchoolList = async () => {
    try {
      const response = await axiosInstance.get('/metrics/school-devices', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      const data = response.data.data || [];
      const uniqueSchools = [...new Set(data.map((d) => d.school))];
      const mapping = {};
      data.forEach(({ school, ip_address }) => {
        mapping[school] = ip_address;
      });

      setSchoolList(uniqueSchools);
      setIpMap(mapping);
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchSchoolList();
  }, []);

  // Сбрасываем страницу на 1 при изменении фильтров
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSchool, startDate, endDate]);

  // Загружаем результаты при изменении страницы или фильтров (с debounce для фильтров)
  useEffect(() => {
    const loadData = async () => {
      // Строим параметры фильтрации
      const params = {};
      if (selectedSchool && ipMap[selectedSchool]) params.ip = ipMap[selectedSchool];
      const start = serializeDate(startDate, false);
      const end = serializeDate(endDate, true);
      if (start) params.start = start;
      if (end) params.end = end;

      try {
        const requestParams = {
          ...params,
          page: currentPage,
          limit: itemsPerPage,
        };
        const response = await axiosInstance.get('/metrics/speedtest-results', {
          params: requestParams,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });
        setResults(response.data.data || []);
        if (response.data.pagination?.total !== undefined) {
          setTotalResults(response.data.pagination.total);
        } else {
          setTotalResults(response.data.data?.length || 0);
        }
      } catch (error) {
      }
    };

    // Debounce для фильтров (но не для изменения страницы)
    const delay = schoolList.length > 0 && (selectedSchool || startDate || endDate) ? 300 : 0;
    const timer = setTimeout(() => {
      loadData();
    }, delay);

    return () => clearTimeout(timer);
  }, [currentPage, selectedSchool, startDate, endDate, ipMap, schoolList.length]);

  useEffect(() => {
    let portalRoot = document.getElementById(DATEPICKER_PORTAL_ID);
    let created = false;

    if (!portalRoot) {
      portalRoot = document.createElement('div');
      portalRoot.id = DATEPICKER_PORTAL_ID;
      portalRoot.style.position = 'relative';
      portalRoot.style.zIndex = '9999';
      document.body.appendChild(portalRoot);
      created = true;
    }

    return () => {
      if (created && portalRoot.parentNode) {
        portalRoot.parentNode.removeChild(portalRoot);
      }
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
  };

  const serializeDate = (date, isEndDate = false) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    // Добавляем время: для начала дня - 00:00:00, для конца дня - 23:59:59
    const time = isEndDate ? '23:59:59' : '00:00:00';
    return `${year}-${month}-${day} ${time}`;
  };

  const buildFilters = () => {
    const params = {};
    if (selectedSchool && ipMap[selectedSchool]) params.ip = ipMap[selectedSchool];
    const start = serializeDate(startDate, false);
    const end = serializeDate(endDate, true);
    if (start) params.start = start;
    if (end) params.end = end;
    return params;
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    // fetchResults будет вызван через useEffect при изменении currentPage
  };

  const handleResetFilters = () => {
    setSelectedSchool('');
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);
    // fetchResults будет вызван через useEffect при изменении currentPage
  };

  const handleExportToExcel = async () => {
    try {
      // Строим параметры фильтрации (те же, что используются для отображения)
      const params = {};
      if (selectedSchool && ipMap[selectedSchool]) params.ip = ipMap[selectedSchool];
      const start = serializeDate(startDate, false);
      const end = serializeDate(endDate, true);
      if (start) params.start = start;
      if (end) params.end = end;

      // Загружаем все данные без пагинации для экспорта
      const response = await axiosInstance.get('/metrics/speedtest-results', {
        params: {
          ...params,
          page: 1,
          limit: totalResults || 10000, // Загружаем все записи или максимум 10000
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      const allResults = response.data.data || [];

      // Подготавливаем данные для экспорта
      const exportData = allResults.map((row, index) => {
        const schoolName = Object.keys(ipMap).find((key) => ipMap[key] === row.ip_address) || row.ip_address;
        return {
          '№': index + 1,
          'Объект': schoolName,
          'IP адрес': row.ip_address || '',
          'Дата и время': new Date(row.timestamp).toLocaleString('ru-KZ', {
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }),
          'Download (Mbps)': row.download_mbps != null ? parseFloat(row.download_mbps).toFixed(2) : '—',
          'Upload (Mbps)': row.upload_mbps != null ? parseFloat(row.upload_mbps).toFixed(2) : '—',
          'Packet Loss (%)': row.packet_loss != null ? parseFloat(row.packet_loss).toFixed(2) : '—',
        };
      });

      // Создаем рабочую книгу Excel
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Speedtest Results');

      // Настраиваем ширину колонок
      const colWidths = [
        { wch: 5 },   // №
        { wch: 30 },  // Объект
        { wch: 15 },  // IP адрес
        { wch: 20 },  // Дата и время
        { wch: 15 },  // Download
        { wch: 15 },  // Upload
        { wch: 15 },  // Packet Loss
      ];
      ws['!cols'] = colWidths;

      // Генерируем имя файла с текущей датой
      const date = new Date().toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).replace(/\//g, '-');
      const fileName = `Speedtest_Results_${date}.xlsx`;

      // Сохраняем файл
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      alert(t('speedtestResults.exportError'));
    }
  };

  const totalPages = Math.ceil(totalResults / itemsPerPage);

  return (
    <div className="relative flex min-h-screen flex-col overflow-clip bg-surface-base text-muted-100">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-brand-radial opacity-50" />
      <Header isSidebarVisible={isSidebarVisible} toggleSidebar={toggleSidebar} userEmail={userEmail} userRole={userRole} />
      <div className="flex flex-1">
        <Sidebar 
          onLogout={onLogout || handleLogout} 
          userRole={userRole} 
          isSidebarVisible={isSidebarVisible}
          onClose={() => setSidebarVisible(false)}
        />

        <main className="relative flex min-w-0 flex-1 flex-col gap-4 bg-surface-base/60 px-3 py-4 transition-all duration-300 ease-brand sm:gap-6 sm:px-4 sm:py-6 lg:px-10">
          <h1 className="font-display text-xl text-white md:text-2xl">{t('speedtestResults.title')}</h1>

          <FiltersSection
            schoolList={schoolList}
            selectedSchool={selectedSchool}
            onSchoolChange={setSelectedSchool}
            startDate={startDate}
            onStartDateChange={setStartDate}
            endDate={endDate}
            onEndDateChange={setEndDate}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
            isFiltered={isFiltered}
            datePickerPortalId={DATEPICKER_PORTAL_ID}
            calendarClassName="speedtest-datepicker"
            popperClassName="speedtest-datepicker-popper"
          />

          <section className={`relative flex flex-col gap-4 rounded-3xl p-6 ${surface.section}`}>
            <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="font-display text-lg text-white">{t('speedtestResults.results')}</h2>
                <p className="mt-1 text-sm text-muted-400">
                  {t('speedtestResults.foundRecords', { total: totalResults, shown: results.length })}
                </p>
              </div>
              <button
                type="button"
                onClick={handleExportToExcel}
                className="inline-flex items-center gap-1.5 rounded-xl bg-white/[0.04] px-3 py-2 text-xs font-medium text-muted-300 transition hover:bg-white/[0.08] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              >
                <span className="material-icons text-sm">download</span>
                {t('speedtestResults.exportExcel')}
              </button>
            </header>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
                  <tr className="border-b border-white/[0.04]">
                    {tableColumns.map((column) => (
                      <th key={column.key} className="px-4 py-3 text-left font-medium">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.map((row, index) => {
                    const schoolName = Object.keys(ipMap).find((key) => ipMap[key] === row.ip_address) || row.ip_address;
                    return (
                      <tr key={`${row.ip_address}-${row.timestamp}-${index}`} className="border-b border-white/[0.04] transition hover:bg-white/[0.04]">
                        <td className="px-4 py-3 text-white">{schoolName}</td>
                        <td className="px-4 py-3 text-muted-300">
                          {new Date(row.timestamp).toLocaleString('ru-KZ', {
                            hour12: false,
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="px-4 py-3 text-accent-cyan">
                          {row.download_mbps != null ? `${row.download_mbps} Mbps` : '-'}
                        </td>
                        <td className="px-4 py-3 text-accent-pink">
                          {row.upload_mbps != null ? `${row.upload_mbps} Mbps` : '-'}
                        </td>
                        <td className="px-4 py-3 text-muted-200">
                          {row.packet_loss != null ? `${row.packet_loss} %` : '-'}
                        </td>
                      </tr>
                    );
                  })}

                  {results.length === 0 && (
                    <tr>
                      <td colSpan={tableColumns.length} className="px-4 py-16 text-center text-sm text-muted-400">
                        {t('speedtestResults.noData')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <nav className="flex items-center justify-between gap-4 pt-2 text-sm text-muted-300">
                <span>
                  {t('speedtestResults.pageOf', { current: currentPage, total: totalPages })}
                </span>
                <div className="inline-flex items-center gap-1 rounded-xl bg-white/[0.04] p-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-300 transition hover:bg-white/[0.08] hover:text-white disabled:opacity-40"
                    disabled={currentPage === 1}
                  >
                    <span className="material-icons text-sm">chevron_left</span>
                  </button>
                  <span className="px-2 text-xs text-muted-300">
                    {currentPage}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-300 transition hover:bg-white/[0.08] hover:text-white disabled:opacity-40"
                    disabled={currentPage === totalPages}
                  >
                    <span className="material-icons text-sm">chevron_right</span>
                  </button>
                </div>
              </nav>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default SpeedtestResults;
